import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Profile } from '../lib/supabase';

interface AuthState {
  user: Profile | null;
  profile: Profile | null;
  loading: boolean;
  isAdmin: boolean;
  credits: number;
  refetchProfile: () => void;
}

export const useAuth = (): AuthState => {
  const [user, setUser] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetchProfile = () => setFetchTrigger(t => t + 1);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        localStorage.removeItem('isDemoMode');
        localStorage.removeItem('isLoggedIn');
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        localStorage.removeItem('isDemoMode');
        localStorage.removeItem('isLoggedIn');
        fetchProfile(session.user.id);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchTrigger]);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      if (data) {
        setUser(data as Profile);
        return;
      }
      // Profile doesn't exist yet — create it
      throw new Error('No profile found');
    } catch {
      const { data: authData } = await supabase.auth.getUser();
      if (authData.user) {
        const meta = authData.user.user_metadata || {};
        const defaultProfile: Partial<Profile> = {
          id: authData.user.id,
          email: authData.user.email || '',
          full_name: meta.full_name || meta.name || '',
          avatar_url: meta.avatar_url || meta.picture || '',
          plan: 'free',
          credits: 100,
          trial_active: true,
          trial_end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        const { data: newProfile } = await supabase
          .from('profiles')
          .upsert([defaultProfile], { onConflict: 'id', ignoreDuplicates: false })
          .select()
          .maybeSingle();
        if (newProfile) setUser(newProfile as Profile);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    profile: user,
    loading,
    isAdmin: user?.plan === 'admin' || user?.is_admin === true || user?.role === 'admin',
    credits: user?.credits ?? 0,
    refetchProfile,
  };
};
